/**
 * WHAT THIS ONE PERSON PAYS.
 *
 * The owner, 2026-08-11, from inside the portal, entering his regulars by hand:
 *
 * > "I've just manually begun adding some of the customers since you seem incapable of doing what I
 * >  asked. But I have discovered you never gave me the ability to customize pricing for certain
 * >  items or provide automatic discounts."
 *
 * The capability shipped with migration 0088 and two assistant tools. No screen exposed it, so the
 * man doing the work himself reached the end of the form and stopped. This is that screen.
 *
 * ## The one sentence it has to be able to say back to him
 *
 * > "Mark Pikor. Note that he gets the Kozel for $6 instead of the advertised $9. He also gets 10%
 * >  off everything else... (the 10% does not apply to the beer...)"
 *
 * Two different kinds of thing about one person — A NAMED PRICE ON A NAMED ITEM, and A STANDING
 * DISCOUNT ON EVERYTHING ELSE — and the rule that joins them: the discount never touches an item
 * that has its own price. His Kozel is $6.00, never $5.40.
 *
 * That rule is structural in `domains/pricing/arrangement.ts` (a named size is skipped by the sweep,
 * so there is only ever one row for the beer and it is a price rather than a percentage). But
 * structural is not the same as legible, and the parenthesis in his sentence is him ASKING whether
 * the software understands it. So the rule is printed on this screen, twice — once before either
 * kind of thing exists, and once beside the percentage with his real count, his real item and the
 * real arithmetic in it. A tooltip nobody opens would not have answered him.
 *
 * ## What this screen deliberately does not do
 *
 * It does not price anything. Every figure it prints comes from the server: the read-back carries
 * each named price beside the card's own price and the promotions that beat it, and the picker
 * carries the card price on each option. A screen that worked out what somebody pays would be a
 * second pricing engine, and the two would disagree within a year.
 *
 * There is NO LEMPIRA BOX, on purpose rather than by omission. The menu is priced in dollars and
 * the lempira figure is worked out at the rate when the guest pays; a pinned lempira price is a
 * separate, deliberate capability with its own provenance, and it lives on the Prices screen.
 */
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { detailKey, listKey } from '../../api/queryKeys';
import { SEARCHABLE, SearchPicker, type VariantHit } from '../../components/SearchPicker';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatMoney, type NumberInputValue, toMinorUnits, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

/** One thing they have a price of their own for, as the server reads it back. */
interface NamedPrice {
  variantId: string;
  /** "Kozel — Bottle". Resolved server-side, so no screen fetches the catalog to render it. */
  label: string;
  usdMinor: number;
  menuUsdMinor: number | null;
  /** Windows that price this BELOW their own price — the reason their number is a ceiling. */
  promotions: { name: string; usdMinor: number }[];
}

export interface Arrangement {
  customerId: string;
  customerName: string;
  active: boolean;
  startedAt: string | null;
  named: NamedPrice[];
  everythingElseBp: number | null;
  sweptCount: number;
  /** Card items reached neither by a named price nor by the percentage (menu grew after set). */
  notCoveredCount: number;
}

/** One of the bar's own discounts, judged for whether it can stand as a per-person arrangement. */
interface StandingOption {
  discountId: string;
  name: string;
  percentOff: number | null;
  offerable: boolean;
  whyNot: string | null;
}

const ARRANGEMENT_PATH = '/admin/v1/customers/{id}/price-arrangement';
const STANDING_DISCOUNTS_PATH = '/admin/v1/customers/standing-discounts';
const arrangementPath = (customerId: string) => `/admin/v1/customers/${customerId}/price-arrangement`;

/** Basis points, always negative, → the whole percent a person types. -1000 → 10. */
const bpToPercent = (bp: number) => Math.abs(bp) / 100;
const percentToBp = (percent: number) => -Math.round(percent * 100);

/**
 * The number the rule forbids: their own price with the standing discount taken off it again.
 *
 * Computed here deliberately, and it is the ONE figure on this screen that does not come from the
 * server — because it is the figure that must never happen. "the Kozel stays $6.00, not $5.40" is
 * the sentence that answers his parenthesis, and it cannot be written without naming the wrong
 * answer beside the right one.
 */
const discountedAgain = (usdMinor: number, bp: number) =>
  usdMinor - Math.round((usdMinor * Math.abs(bp)) / 10_000);

export function PriceArrangement({ customerId }: { customerId: string }) {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  const [adding, setAdding] = useState(false);
  const [settingDiscount, setSettingDiscount] = useState(false);

  const query = useQuery({
    queryKey: detailKey(ARRANGEMENT_PATH, customerId),
    queryFn: () => rawApi<Arrangement>('GET', arrangementPath(customerId)),
    enabled: Boolean(customerId),
  });

  /**
   * Whether the bar sells through a delivery platform at all.
   *
   * The rule — a personal arrangement does not follow somebody onto a delivery platform — is real
   * and is keyed on the channel's TYPE. Deco Stop has no such channel today, so the line renders
   * nothing: a rule about a channel he does not have is noise, and the same rule the day he adds
   * one is money.
   */
  const channels = useQuery({
    queryKey: listKey('/admin/v1/channels/channels'),
    queryFn: () => rawApi<{ type?: string }[]>('GET', '/admin/v1/channels/channels'),
  });
  const sellsThroughAPlatform = (channels.data ?? []).some((row) => row.type === 'deliveryPlatform');

  const arrangement = query.data;

  /** Every write is the WHOLE arrangement — see the PUT door: what the screen no longer says, they no longer pay. */
  const save = useMutation({
    mutationFn: (next: {
      items: { variantId: string; usdMinor: number }[];
      everythingElseBp?: number | null;
      fromDiscountId?: string | null;
    }) => rawApi<Arrangement>('PUT', arrangementPath(customerId), next),
    onSuccess: (saved) => {
      queryClient.setQueryData(detailKey(ARRANGEMENT_PATH, customerId), saved);
      void queryClient.invalidateQueries({ queryKey: detailKey(ARRANGEMENT_PATH, customerId) });
      setAdding(false);
      setSettingDiscount(false);
      notifyDone('Saved', `What ${saved.customerName} pays has been updated.`);
    },
    onError: (err) => notifyError(err, 'save these prices'),
  });

  const end = useMutation({
    mutationFn: () => rawApi<Arrangement>('DELETE', arrangementPath(customerId)),
    onSuccess: (saved) => {
      queryClient.setQueryData(detailKey(ARRANGEMENT_PATH, customerId), saved);
      notifyDone(
        'Ended',
        `${saved.customerName} pays the menu price from now on. Everything already rung keeps its price.`,
      );
    },
    onError: (err) => notifyError(err, 'end this arrangement'),
  });

  if (query.isLoading) return <Text c="dimmed">Loading…</Text>;
  if (query.isError || !arrangement) {
    return (
      <Alert color="red" title="This could not be read">
        What this person pays could not be read just now. Nothing has changed. Refresh the page to try again.
      </Alert>
    );
  }

  const { named, everythingElseBp, sweptCount, notCoveredCount, customerName } = arrangement;
  const percent = everythingElseBp == null ? null : bpToPercent(everythingElseBp);
  const asItems = named.map((n) => ({ variantId: n.variantId, usdMinor: n.usdMinor }));

  const saveWith = (
    items: { variantId: string; usdMinor: number }[],
    bp: number | null,
    fromDiscountId?: string | null,
  ) => {
    if (fromDiscountId) {
      save.mutate({ items, fromDiscountId });
    } else {
      save.mutate({ items, everythingElseBp: bp });
    }
  };

  // ------------------------------------------------------------------ nothing agreed yet

  if (!arrangement.active) {
    return (
      <Stack gap="md">
        <Text>Right now {customerName} pays the menu price for everything, like anybody else.</Text>

        {adding ? (
          <NamePricePanel
            customerName={customerName}
            locationId={locationId}
            alreadyNamed={[]}
            busy={save.isPending}
            onCancel={() => setAdding(false)}
            onSave={(item) => saveWith([item], everythingElseBp)}
          />
        ) : settingDiscount ? (
          <StandingDiscountPanel
            customerName={customerName}
            named={[]}
            busy={save.isPending}
            current={null}
            onCancel={() => setSettingDiscount(false)}
            onSavePercent={(bp) => saveWith(asItems, bp)}
            onSaveFromDiscount={(discountId) => saveWith(asItems, null, discountId)}
          />
        ) : (
          <Group align="flex-start" gap="xl" wrap="wrap">
            <Stack gap={4} style={{ maxWidth: 320 }}>
              <Button variant="light" onClick={() => setAdding(true)}>
                Name a price for one item
              </Button>
              <Text size="xs" c="dimmed">
                A price only they pay — one item, at a figure you agree, against what the menu charges
                everybody else.
              </Text>
            </Stack>
            <Stack gap={4} style={{ maxWidth: 320 }}>
              <Button variant="light" onClick={() => setSettingDiscount(true)}>
                Set a standing discount
              </Button>
              <Text size="xs" c="dimmed">
                A percentage off everything else on the menu, applied by itself.
              </Text>
            </Stack>
          </Group>
        )}

        {/*
          THE LINKING RULE, STATED BEFORE EITHER KIND OF THING EXISTS. It is the answer to the
          parenthesis in his own sentence, and by the time he has typed both halves he has already
          decided what he thinks the software does.
        */}
        <Text size="sm" c="dimmed">
          An item with a named price is never also discounted.
        </Text>
      </Stack>
    );
  }

  // ------------------------------------------------------------------ reading it back

  return (
    <Stack gap="lg">
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text fw={600}>{readBackSentence(arrangement)}</Text>
            {named.length > 0 && percent != null && (
              <Text size="sm" c="dimmed">
                The {percent}% never touches{' '}
                {named.length === 1 ? shortLabel(named[0]!.label) : 'those items'}.
              </Text>
            )}
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <Button
              variant="subtle"
              color="red"
              loading={end.isPending}
              onClick={() =>
                modals.openConfirmModal({
                  title: `End ${customerName}'s own prices?`,
                  children: (
                    <Stack gap="xs">
                      <Text size="sm">They go back to paying the menu price for everything.</Text>
                      {/*
                        THE OPEN-TAB CLAUSE IS NOT OPTIONAL. A line's price is fixed when it is rung
                        and nothing reprices it afterwards — so every past bill stays explicable, and
                        a tab that is still open tonight starts charging the menu price for whatever
                        is rung onto it next. "Old bills keep the old arrangement" would be true and
                        would leave him expecting the second half to be true as well.
                      */}
                      <Text size="sm">
                        Everything already rung keeps the price it was rung at, and nothing is deleted, so
                        every past bill still explains itself. Anything rung from now on is the menu price —
                        including on a tab that is still open.
                      </Text>
                      <Text size="sm">
                        Any discount they are entitled to still applies, and comes off the menu price now
                        rather than off their own.
                      </Text>
                      <Text size="sm">
                        You can agree a new arrangement any time by naming the prices again.
                      </Text>
                    </Stack>
                  ),
                  labels: { confirm: 'End it', cancel: 'Keep it' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => end.mutate(),
                })
              }
            >
              End this arrangement
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* ---------------------------------------------------------------- the named prices */}

      {named.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Prices only they pay
          </Text>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Item</Table.Th>
                <Table.Th>They pay</Table.Th>
                <Table.Th>Menu price</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {named.map((row) => (
                <NamedPriceRow
                  key={row.variantId}
                  row={row}
                  busy={save.isPending}
                  hasStandingDiscount={everythingElseBp != null}
                  onChange={(usdMinor) =>
                    saveWith(
                      asItems.map((i) => (i.variantId === row.variantId ? { ...i, usdMinor } : i)),
                      everythingElseBp,
                    )
                  }
                  onRemove={() =>
                    saveWith(
                      asItems.filter((i) => i.variantId !== row.variantId),
                      everythingElseBp,
                    )
                  }
                />
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}

      {adding ? (
        <NamePricePanel
          customerName={customerName}
          locationId={locationId}
          alreadyNamed={named.map((n) => n.variantId)}
          busy={save.isPending}
          onCancel={() => setAdding(false)}
          onSave={(item) => saveWith([...asItems, item], everythingElseBp)}
        />
      ) : (
        <div>
          <Button variant="light" size="sm" onClick={() => setAdding(true)}>
            Name a price for one item
          </Button>
        </div>
      )}

      <Divider />

      {/* ---------------------------------------------------------- the standing discount */}

      {settingDiscount ? (
        <StandingDiscountPanel
          customerName={customerName}
          named={named}
          busy={save.isPending}
          current={percent}
          onCancel={() => setSettingDiscount(false)}
          onSavePercent={(bp) => saveWith(asItems, bp)}
          onSaveFromDiscount={(discountId) => saveWith(asItems, null, discountId)}
        />
      ) : percent == null ? (
        <Stack gap={4} align="flex-start">
          <Button variant="light" size="sm" onClick={() => setSettingDiscount(true)}>
            Set a standing discount
          </Button>
          <Text size="xs" c="dimmed">
            A percentage off everything else on the menu, applied by itself.
          </Text>
        </Stack>
      ) : (
        <Stack gap="xs">
          <Group gap="xs" align="baseline">
            <Text fw={600} size="sm">
              Standing discount
            </Text>
            <Badge variant="light">{percent}% off everything else</Badge>
          </Group>
          {/* A COUNT, NEVER A LIST. The sweep writes one row per size and bundle on the card. */}
          <Text size="sm">
            Everything else on the menu — {sweptCount} {sweptCount === 1 ? 'item' : 'items'} — is {percent}%
            off.
          </Text>
          {notCoveredCount > 0 && (
            <Alert color="yellow" title="The menu has grown since this was set">
              {notCoveredCount} {notCoveredCount === 1 ? 'item is' : 'items are'} on the card but not under
              this discount. Save the standing discount again to include{' '}
              {notCoveredCount === 1 ? 'it' : 'them'}.
            </Alert>
          )}
          <ExclusionRule named={named} percent={percent} />
          <Group gap="xs">
            <Button variant="subtle" size="compact-sm" onClick={() => setSettingDiscount(true)}>
              Change
            </Button>
            <Button
              variant="subtle"
              size="compact-sm"
              color="red"
              onClick={() =>
                modals.openConfirmModal({
                  title: 'Take the standing discount off?',
                  children: (
                    <Text size="sm">
                      They go back to paying the menu price for everything except the items named above, which
                      keep the prices you agreed.
                    </Text>
                  ),
                  labels: { confirm: 'Take it off', cancel: 'Keep it' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => saveWith(asItems, null),
                })
              }
            >
              Take it off
            </Button>
          </Group>
        </Stack>
      )}

      {/* ------------------------------------------------------- what else is true about this */}

      <Divider />

      <Stack gap={6}>
        {/*
          MONEY LEAVING THE BUILDING UNDER SOMEBODY ELSE'S NAME. A driver arguing about a statement
          is precisely the moment he needs to already know this, so it is on the screen where the
          arrangement is agreed rather than in the referrals section where the argument happens.
        */}
        <Text size="sm" c="dimmed">
          A partner who brings them in earns no commission on anything charged under this arrangement.
        </Text>
        {sellsThroughAPlatform && (
          <Text size="sm" c="dimmed">
            These prices do not follow them onto a delivery platform. An order that comes through one is
            charged the menu price.
          </Text>
        )}
        {/*
          He is the one who will hear "the register said $9". The bill is re-priced server-side and is
          right either way, but being surprised by it at the counter is worse than knowing.
        */}
        <Text size="sm" c="dimmed">
          A register that has gone offline may show them the menu price. The bill is worked out here and is
          right either way.
        </Text>
      </Stack>
    </Stack>
  );
}

/**
 * THE EXCLUSION RULE, with his real count, his real item and the real arithmetic in it.
 *
 * "the 10% does not apply to the beer" is the half of his sentence he put in brackets, which is how
 * people write the thing they are least sure the other party has understood. Naming the wrong answer
 * — $5.40 — beside the right one is what makes it readable back as a fact rather than a promise.
 */
function ExclusionRule({ named, percent }: { named: NamedPrice[]; percent: number }) {
  const bp = percentToBp(percent);
  const example = named[0];
  return (
    <Stack gap={4}>
      <Text size="sm">
        It applies by itself the moment a tab is on their name — nobody at the register taps anything.
      </Text>
      {named.length > 0 && example ? (
        <Text size="sm" fw={500}>
          It never touches the {named.length} {named.length === 1 ? 'item' : 'items'} they have a named price
          for: {shortLabel(example.label)} stays {formatMoney(example.usdMinor, 'USD')}, not{' '}
          {formatMoney(discountedAgain(example.usdMinor, bp), 'USD')}.
        </Text>
      ) : (
        <Text size="sm" fw={500}>
          It will never touch an item you give a price of its own — that price stands as agreed.
        </Text>
      )}
      {/* The surprising half of "never discounted twice": the comparison can go the other way. */}
      <Text size="sm" c="dimmed">
        If a discount off the menu price would ever beat one of their named prices, they get that instead —
        never both.
      </Text>
      {/* The server's own warning, and the only person who can act on it is reading this. */}
      <Text size="sm" c="dimmed">
        Anything you add to the menu later is not covered until you set this again.
      </Text>
    </Stack>
  );
}

/** One named price, with what the menu charges beside it and what can still beat it underneath. */
function NamedPriceRow({
  row,
  busy,
  hasStandingDiscount,
  onChange,
  onRemove,
}: {
  row: NamedPrice;
  busy: boolean;
  hasStandingDiscount: boolean;
  onChange: (usdMinor: number) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState<NumberInputValue>(row.usdMinor / 100);
  const typedMinor = toMinorUnits(price);
  const aboveMenu = row.menuUsdMinor != null && typedMinor != null && typedMinor > row.menuUsdMinor;

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{row.label}</Text>
        <PromotionNote row={row} />
      </Table.Td>
      <Table.Td>
        {editing ? (
          <Stack gap={4}>
            <NumberInput
              aria-label={`What they pay for ${row.label}`}
              value={price}
              onChange={setPrice}
              prefix="$ "
              decimalScale={2}
              fixedDecimalScale
              min={0}
              w={140}
              size="xs"
            />
            {aboveMenu && (
              <Text size="xs" c="red">
                {aboveMenuRefusal(row.label, row.menuUsdMinor!, typedMinor!)}
              </Text>
            )}
          </Stack>
        ) : (
          <Text size="sm" fw={600}>
            {formatMoney(row.usdMinor, 'USD')}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {row.menuUsdMinor == null ? 'Not on the menu' : formatMoney(row.menuUsdMinor, 'USD')}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap" justify="flex-end">
          {editing ? (
            <>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => {
                  setPrice(row.usdMinor / 100);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="compact-xs"
                loading={busy}
                disabled={typedMinor == null || typedMinor <= 0 || aboveMenu}
                onClick={() => {
                  onChange(typedMinor!);
                  setEditing(false);
                }}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button size="compact-xs" variant="subtle" onClick={() => setEditing(true)}>
                Change
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={() =>
                  modals.openConfirmModal({
                    title: `Stop pricing ${shortLabel(row.label)} for them?`,
                    children: (
                      <Text size="sm">
                        They go back to paying the menu price for this.
                        {hasStandingDiscount
                          ? ' With a standing discount set, this item then falls under it.'
                          : ''}
                      </Text>
                    ),
                    labels: { confirm: 'Remove it', cancel: 'Keep it' },
                    confirmProps: { color: 'red' },
                    onConfirm: onRemove,
                  })
                }
              >
                Remove
              </Button>
            </>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * WHAT CAN STILL BEAT THIS NUMBER — because his number is a CEILING and never a floor.
 *
 * The better price always wins, so a promotion that goes below what he agreed is what the guest is
 * charged. Where the server has named such a window this says so as a fact about this item; where it
 * has not, the honest sentence is the conditional one, and it still must not be omitted.
 */
function PromotionNote({ row }: { row: NamedPrice }) {
  if (row.promotions.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        If a promotion ever prices this lower, they pay the promotion price.
      </Text>
    );
  }
  const best = row.promotions[0]!;
  return (
    <Text size="xs" c="dimmed">
      {best.name} prices this at {formatMoney(best.usdMinor, 'USD')}, so they pay{' '}
      {formatMoney(best.usdMinor, 'USD')} then.
    </Text>
  );
}

/**
 * Naming a price. The item is TYPED, never chosen off a list of everything.
 *
 * Each option carries the published price for that size, so the size is chosen with its own price in
 * view and can never be guessed at — a product has up to fourteen of them and the parent is never
 * priced, which is why the picker searches sizes rather than dishes.
 */
function NamePricePanel({
  customerName,
  locationId,
  alreadyNamed,
  busy,
  onCancel,
  onSave,
}: {
  customerName: string;
  locationId: string | undefined;
  alreadyNamed: string[];
  busy: boolean;
  onCancel: () => void;
  onSave: (item: { variantId: string; usdMinor: number }) => void;
}) {
  const [picked, setPicked] = useState<VariantHit | null>(null);
  const [price, setPrice] = useState<NumberInputValue>('');
  /**
   * Picking something they already have a price for, said out loud.
   *
   * It used to be a silent `return` — the option was clicked, the panel did not move, and the only
   * available reading was that the screen was broken. There is a row for it in the table above and
   * `Change` on that row is where the price is edited; this points at it rather than quietly adding
   * a second statement about one size.
   */
  const [alreadyHasOne, setAlreadyHasOne] = useState<string | null>(null);

  const menu = picked?.everydayUsdMinor ?? null;
  const typedMinor = toMinorUnits(price);
  const aboveMenu = menu != null && typedMinor != null && typedMinor > menu;
  const sameAsMenu = menu != null && typedMinor != null && typedMinor === menu;
  const saveable = typedMinor != null && typedMinor > 0 && !aboveMenu;

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="md">
        {!picked ? (
          <>
            <SearchPicker
              resource={SEARCHABLE.variant}
              params={{ locationId }}
              label="Which item?"
              placeholder="Start typing an item"
              description="Type a few letters. Sizes are listed with what the menu charges for them."
              onPick={(hit) => {
                // A size already priced for them is not a second arrangement; it is the row above.
                if (alreadyNamed.includes(hit.variantId)) {
                  setAlreadyHasOne(`${hit.itemName} — ${hit.sizeName}`);
                  return;
                }
                setAlreadyHasOne(null);
                setPicked(hit);
              }}
            />
            {alreadyHasOne && (
              <Text size="sm" c="dimmed">
                {alreadyHasOne} already has a price of its own above — use Change on that row.
              </Text>
            )}
          </>
        ) : (
          <>
            <Group justify="space-between" align="center">
              <Text fw={600}>
                {picked.itemName} — {picked.sizeName}
              </Text>
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={() => {
                  setPicked(null);
                  setPrice('');
                }}
              >
                Choose a different item
              </Anchor>
            </Group>

            {/* THE COMPARISON NEVER DISAPPEARS. "$6.00 against $9.00" in one glance is the point. */}
            <Group align="flex-end" gap="xl" wrap="wrap">
              <NumberInput
                label={`What ${customerName} pays`}
                aria-label={`What ${customerName} pays`}
                value={price}
                onChange={setPrice}
                prefix="$ "
                decimalScale={2}
                fixedDecimalScale
                min={0}
                w={160}
              />
              <Stack gap={0}>
                <Text size="xs" c="dimmed">
                  Menu price
                </Text>
                <Text fw={600}>{menu == null ? 'Not on the menu' : formatMoney(menu, 'USD')}</Text>
              </Stack>
              {menu != null && typedMinor != null && typedMinor < menu && (
                <Text size="sm" c="teal">
                  {formatMoney(menu - typedMinor, 'USD')} less
                </Text>
              )}
            </Group>

            {aboveMenu && (
              <Alert color="red">
                {aboveMenuRefusal(`${picked.itemName} — ${picked.sizeName}`, menu!, typedMinor!)}
              </Alert>
            )}
            {sameAsMenu && (
              <Text size="sm" c="dimmed">
                Same as the menu — this holds it out of the standing discount below.
              </Text>
            )}

            <Text size="sm" c="dimmed">
              Priced in dollars. The lempira figure is worked out at the rate when they pay.
            </Text>
          </>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!picked || !saveable}
            onClick={() => picked && onSave({ variantId: picked.variantId, usdMinor: typedMinor! })}
          >
            Save this price
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

/**
 * The standing discount: EITHER one of the bar's own discounts of the right shape, OR a percentage
 * typed for this person. One decision — not a mode switch with two panels.
 *
 * The number is always a COPY. There is no live link back to the discount row, so editing Employee
 * later does not move anyone already arranged. The copy is what the screen must say out loud.
 */
function StandingDiscountPanel({
  customerName,
  named,
  current,
  busy,
  onCancel,
  onSavePercent,
  onSaveFromDiscount,
}: {
  customerName: string;
  named: NamedPrice[];
  current: number | null;
  busy: boolean;
  onCancel: () => void;
  onSavePercent: (bp: number) => void;
  onSaveFromDiscount: (discountId: string) => void;
}) {
  const [percent, setPercent] = useState<NumberInputValue>(current ?? '');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const options = useQuery({
    queryKey: listKey(STANDING_DISCOUNTS_PATH),
    queryFn: () => rawApi<{ options: StandingOption[] }>('GET', STANDING_DISCOUNTS_PATH),
  });
  const offerable = (options.data?.options ?? []).filter((o) => o.offerable);
  const notOfferable = (options.data?.options ?? []).filter((o) => !o.offerable);
  const typed = typedNumber(percent);
  const typedValid = typed != null && typed > 0 && typed < 99 && pickedId == null;
  const picked = offerable.find((o) => o.discountId === pickedId) ?? null;
  const previewPercent = picked?.percentOff ?? (typedValid ? typed : null);

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="md">
        {offerable.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Use one of the bar’s discounts
            </Text>
            <Text size="xs" c="dimmed">
              The percentage is copied now. Changing the discount later will not move {customerName}.
            </Text>
            <Group gap="xs" wrap="wrap">
              {offerable.map((opt) => (
                <Button
                  key={opt.discountId}
                  size="sm"
                  variant={pickedId === opt.discountId ? 'filled' : 'light'}
                  onClick={() => {
                    setPickedId(opt.discountId);
                    setPercent('');
                  }}
                >
                  {opt.name}
                  {opt.percentOff != null ? ` · ${opt.percentOff}%` : ''}
                </Button>
              ))}
            </Group>
          </Stack>
        )}

        <Group align="flex-end" gap="xs">
          <NumberInput
            label={offerable.length > 0 ? 'Or type a percentage' : 'Standing discount'}
            aria-label="Standing discount"
            value={percent}
            onChange={(v) => {
              setPercent(v);
              setPickedId(null);
            }}
            suffix=" %"
            min={0}
            max={99}
            w={140}
          />
          <Text pb={8}>off everything else on the menu.</Text>
        </Group>

        {previewPercent != null && <ExclusionRule named={named} percent={previewPercent} />}

        {notOfferable.length > 0 && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--mantine-color-dimmed)' }}>
              {offerable.length === 0
                ? `Why none of the bar’s ${notOfferable.length} discounts can stand here`
                : `Why ${notOfferable.length} other discounts cannot stand here`}
            </summary>
            <Stack gap={4} mt="xs">
              {notOfferable.map((opt) => (
                <Text key={opt.discountId} size="xs" c="dimmed">
                  <Text span fw={500} c="dimmed">
                    {opt.name}.
                  </Text>{' '}
                  {opt.whyNot}
                </Text>
              ))}
            </Stack>
          </details>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={picked == null && !typedValid}
            onClick={() => {
              if (picked) onSaveFromDiscount(picked.discountId);
              else if (typedValid) onSavePercent(percentToBp(typed!));
            }}
          >
            {current == null ? 'Set this discount' : 'Save this discount'}
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          {customerName} keeps every price you have named for them; this covers what is left.
        </Text>
      </Stack>
    </Paper>
  );
}

/**
 * The refusal, in the server's own words and with the real numbers in it.
 *
 * The door refuses this and its sentence is the owner's copy — four sentences rather than "invalid
 * price", because it has to say what the menu charges, why naming more changes nothing, and what to
 * type instead. It is mirrored here so he meets it while typing rather than after a failed save; the
 * server stays the authority, and `errors.ts` shows its words if one ever gets past this.
 */
function aboveMenuRefusal(label: string, menuUsdMinor: number, typedMinor: number): string {
  const menu = formatMoney(menuUsdMinor, 'USD');
  return (
    `${label} already costs ${menu} on the menu, so naming a price of ${formatMoney(typedMinor, 'USD')} ` +
    'would change nothing — they are always charged the better of their price and the menu, and an ' +
    `arrangement can only ever bring a price down. Name ${menu} or less. Naming exactly ${menu} is how ` +
    'you keep it off a blanket percentage.'
  );
}

/** "Kozel — Bottle" → "the Kozel — Bottle", for use inside a sentence. */
function shortLabel(label: string): string {
  return `the ${label}`;
}

/**
 * The whole arrangement as one sentence, which is how he said it and how he should read it back.
 *
 * > "he gets the Kozel for $6 instead of the advertised $9. He also gets 10% off everything else"
 */
export function readBackSentence(arrangement: Arrangement): string {
  const { customerName, named, everythingElseBp } = arrangement;
  const percent = everythingElseBp == null ? null : bpToPercent(everythingElseBp);
  const prices = named.map((n) => `${formatMoney(n.usdMinor, 'USD')} for ${n.label}`);

  if (prices.length === 0 && percent != null) {
    return `${customerName} pays ${percent}% off everything on the menu.`;
  }
  const list =
    prices.length === 1 ? prices[0] : `${prices.slice(0, -1).join(', ')} and ${prices[prices.length - 1]}`;
  if (percent == null) return `${customerName} pays ${list}.`;
  return `${customerName} pays ${list}, and ${percent}% off everything else on the menu.`;
}
